import { Component, type ReactNode } from 'react'
import { tokens } from '@/theme'
import { ErrorPanel } from './ErrorPanel'

interface Props {
  /** Changes when the route does, which clears a caught error. */
  resetKey: string
  children: ReactNode
}

interface State {
  error: Error | null
}

/** The messages Chrome, Firefox and Safari use when a dynamic import fails. */
function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /dynamically imported module|Importing a module script failed|Loading chunk/i.test(message)
}

/**
 * Keeps a render error inside the page that threw it. Without this React
 * unmounts the whole tree and every page goes white, with the cause visible
 * only in the console. The error is shown where the page was, and navigating
 * elsewhere clears it.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) }
  }

  componentDidCatch(error: unknown): void {
    // A route chunk that failed to download stays failed for the page's
    // lifetime: the browser memoises the rejected import and React.lazy its
    // rejection. The background prefetch can hit that on a flaky connection
    // long before anyone navigates, so one reload is the way back. Guarded so
    // a chunk that is truly gone does not reload forever.
    if (!isChunkLoadError(error)) return
    const key = 'keystone:chunk-reload'
    try {
      const last = Number(sessionStorage.getItem(key) ?? 0)
      if (Date.now() - last < 30_000) return
      sessionStorage.setItem(key, String(Date.now()))
    } catch {
      return
    }
    window.location.reload()
  }

  componentDidUpdate(prev: Props): void {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null })
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children
    return (
      <div
        className="keystone-pad"
        style={{
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          padding: `48px ${tokens.containerPadX}px`,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <ErrorPanel error={this.state.error} />
        {import.meta.env.DEV && this.state.error.stack && (
          <pre
            style={{
              marginTop: 16,
              fontSize: 11,
              lineHeight: 1.5,
              color: tokens.ink3,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {this.state.error.stack}
          </pre>
        )}
      </div>
    )
  }
}
